import { useState, useEffect, useCallback } from 'react';
import { getInstallationSyncStatus } from '@/apis/getInstallationSyncStatus';
import type {
  InstallationSyncStatus,
  InstallationSyncProgressData,
} from '@/types/installationSync';

const POLL_INTERVAL_MS = 3000;

export function useInstallationSyncStatus(orgUUID: string | undefined) {
  const [status, setStatus] = useState<InstallationSyncStatus>(null);
  const [progressData, setProgressData] =
    useState<InstallationSyncProgressData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!orgUUID) return;
    try {
      const data = await getInstallationSyncStatus(orgUUID);
      setStatus(data.status);
      setProgressData(data.progress_data ?? null);
      setError(data.Error ?? null);
      return data;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch sync status');
      setStatus(null);
      setProgressData(null);
    }
  }, [orgUUID]);

  useEffect(() => {
    if (!orgUUID) return;
    fetchStatus();
  }, [orgUUID, fetchStatus]);

  useEffect(() => {
    if (status !== 'IN_PROGRESS' || !orgUUID) return;
    const interval = setInterval(async () => {
      const data = await getInstallationSyncStatus(orgUUID);
      setStatus(data.status);
      setProgressData(data.progress_data ?? null);
      setError(data.Error ?? null);
      if (data.status !== 'IN_PROGRESS') {
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [status, orgUUID]);

  return { status, progressData, error, refetch: fetchStatus };
}
